import { useState } from 'react';
import useUTMParams from './hooks/useUTMParams';
import { fetchWithTimeout } from './utils/fetchWithTimeout';

const useSignup = (domain, softwareLicenseVersion) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const { source, channel, campaign } = useUTMParams();

  console.log('UTM Params:', { source, channel, campaign });

  const standardSignup = async (userData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetchWithTimeout(domain + '/account/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...userData, softwareLicenseVersion }),
      });

      if (!response.ok) {
        throw new Error('Failed to register user');
      }

      const data = await response.json();

      setSuccess(true);
      setLoading(false);

      return data;
    } catch (err) {
      setError(err.message);
      setLoading(false);

      return false;
    }
  };

  const marketingUpgradeSignup = async (userData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    delete userData.contactConsent;
    delete userData.country;

    try {
      const response = await fetchWithTimeout(domain + '/account/signup/upgrade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...userData, softwareLicenseVersion }),
      });

      if (!response.ok) {
        throw new Error('Failed to register user');
      }

      const data = await response.json();

      setSuccess(true);
      setLoading(false);

      return data;
    } catch (err) {
      setError(err.message);
      setLoading(false);

      return false;
    }
  };

  const marketingSignup = async (userData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch(domain + '/account/signup/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...userData, source, channel, campaign }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (
          errorData.message &&
          errorData.message.includes('Already opted in to newsletter')
        ) {
          setSuccess(true);
          setLoading(false);
          return { user: true, alreadyRegistered: true };
        }
        throw new Error(errorData.message || 'Failed to register user');
      }

      const data = await response.json();

      if (
        data.message &&
        data.message.includes('Already opted in to newsletter')
      ) {
        setSuccess(true);
        setLoading(false);
        return { user: true, alreadyRegistered: true };
      }

      setSuccess(true);
      setLoading(false);

      return data;
    } catch (err) {
      setError(err.message);
      setLoading(false);

      return false;
    }
  };

  // Tag an address that already has an account, before the details form is filled in. The
  // post carries no profile fields, and the server treats a details-free post as a tag
  // request: it adds the tag and changes nothing else. So the funnel is recorded even if
  // the visitor leaves the form. Failure is not reported: the tag must never block the form.
  const tagExistingContact = async (email) => {
    try {
      await fetch(domain + '/account/signup/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source, channel, campaign }),
      });
    } catch (err) {
      console.error('Error tagging existing contact:', err);
    }
  };

  const signup = (userData, isMarketingSignup, newsletter) => {
    if (newsletter) {
      return marketingSignup(userData);
    } else if (isMarketingSignup) {
      return marketingUpgradeSignup(userData);
    } else {
      return standardSignup(userData);
    }
  };

  const resetPassword = async (token, newPassword) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetchWithTimeout(domain + '/api/users/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: newPassword }),
      });

      if (!response.ok) {
        throw new Error('Failed to reset password');
      }

      const data = await response.json();

      setSuccess(true);
      setLoading(false);

      return data;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return false;
    }
  };

  const forgotPassword = async (email) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetchWithTimeout(domain + '/api/users/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to send password reset email');
      }

      setSuccess(true);
      setLoading(false);
      return true;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return false;
    }
  };

  const verifyAccount = async (token) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await fetchWithTimeout(`${domain}/account/verify/${token}`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (data.message === 'Verified') {
        setSuccess(true);
        setLoading(false);

        return true;
      } else {
        setLoading(false);
        setError(data.message);

        return false;
      }
    } catch (error) {
      setLoading(false);
      setSuccess(false);
      setError(error.message);

      console.error('Error verifying account:', error);
      return false;
    }
  };

  const unsubscribe = async (token) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetchWithTimeout(`${domain}/account/unsubscribe/${token}`, {
        method: 'POST',
        body: JSON.stringify({ source, channel, campaign }),
      });

      const data = await response.json();

      if (data.message === 'Unsubscribed successfully') {
        setSuccess(true);
        setLoading(false);

        return true;
      } else {
        setLoading(false);
        setError(data.message);

        return false;
      }
    } catch (error) {
      setLoading(false);
      setSuccess(false);
      setError(error.message);

      console.error('Error unsubscribing:', error);
      return false;
    }
  };

  return {
    signup,
    tagExistingContact,
    loading,
    error,
    success,
    resetPassword,
    forgotPassword,
    verifyAccount,
    unsubscribe,
  };
};

export default useSignup;
